/**
 * Человеческие объяснения вместо браузерных.
 *
 * WebAuthn возвращает `NotAllowedError` с текстом «The operation either timed
 * out or was not allowed» и ссылкой на раздел спецификации про приватность.
 * Расплывчатость там намеренная: браузер не вправе сообщить сайту, есть ли у
 * человека подходящий ключ, — иначе страница входа превращается в способ это
 * выяснять. Нам эта причина понятна, пользователю — нет: он видит английский
 * текст и ссылку на W3C ровно в тот момент, когда его не пустили в собственный
 * трекер.
 *
 * Поэтому мы говорим не «что случилось» (этого браузер и не сказал), а что
 * делать дальше. Правда при этом не искажается: перечислены оба настоящих
 * варианта, и оба ведут к работающему входу.
 *
 * Объяснений два, потому что один и тот же `NotAllowedError` означает разное.
 * При входе — «подходящего ключа тут нет». При привязке ключа быть и не
 * должно, так что остаются отмена и запрет платформы; на второе и напоролись,
 * когда первый новый пользователь открыл ссылку из письма и остался во
 * встроенном браузере почты.
 */
export function describePasskeyFailure(failure: unknown): string {
  const name = errorName(failure);

  if (name === 'NotAllowedError') {
    return (
      'Passkey на этом устройстве не найден — или вход отменили. ' +
      'Ключ хранится в конкретном браузере или связке ключей устройства, ' +
      'а не в учётной записи: в другом профиле и на другом телефоне его нет. ' +
      'Войдите по почте и привяжите здесь свой ключ — их может быть сколько угодно.'
    );
  }

  return describeCommon(name, failure) ?? 'что-то пошло не так';
}

/** То же самое для привязки ключа: причины у отказа здесь другие. */
export function describePasskeyRegistrationFailure(failure: unknown): string {
  const name = errorName(failure);

  if (name === 'NotAllowedError') {
    return (
      'Ключ не привязан. Чаще всего дело в том, где открыта страница: ' +
      'встроенное окно браузера — то, что открывается прямо в почте или в ' +
      'мессенджере, — passkey заводить не даёт вовсе, и это запрет платформы, ' +
      'а не наш. Добавьте Onlap на экран «Домой» и повторите оттуда. ' +
      'Если вы уже там, проверьте, куда телефону класть ключ: поставщик в ' +
      '«Автозаполнении и паролях» или связка ключей iCloud.'
    );
  }

  return describeCommon(name, failure) ?? 'что-то пошло не так';
}

/**
 * Отказы входа по почте.
 *
 * Сервер отвечает кодами (`login_code_invalid`), и показать их как есть — та
 * же беда, что с английским текстом WebAuthn: правда, из которой не следует,
 * что делать. Поэтому каждое объяснение заканчивается следующим шагом.
 */
export function describeEmailLoginFailure(failure: unknown): string {
  const code =
    typeof failure === 'object' && failure !== null && 'code' in failure
      ? (failure as { code: unknown }).code
      : undefined;

  if (code === 'login_rate_limited') {
    return 'Писем было слишком много. Подождите четверть часа или введите код из письма, которое уже пришло.';
  }
  if (code === 'login_code_invalid') {
    return 'Код не подошёл. Проверьте цифры или запросите новое письмо.';
  }
  if (code === 'login_code_exhausted') {
    return 'Попытки для этого кода закончились. Запросите новое письмо.';
  }
  if (code === 'login_email_daily_limited') {
    return 'На этот адрес сегодня ушло слишком много писем. Введите код из последнего письма или попробуйте завтра.';
  }
  if (code === 'login_service_limited') {
    return 'Сегодня сервис отправил всё, что может. Если аккаунт уже есть, войдите по отпечатку или лицу — или попробуйте завтра.';
  }
  if (code === 'email_undeliverable') {
    return 'Письма на этот адрес не доходят или были отмечены как спам. Проверьте, нет ли в адресе опечатки, или укажите другой.';
  }
  if (code === 'not_invited') {
    return 'Регистрация пока по приглашениям, а этого адреса в списке нет.';
  }
  // Так `fetch` сообщает об отсутствии сети: «Failed to fetch» или «Load failed»
  // в зависимости от браузера, и ни то ни другое не говорит, что делать.
  if (failure instanceof TypeError) {
    return 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.';
  }

  return failure instanceof Error ? failure.message : 'Что-то пошло не так — попробуйте ещё раз.';
}

function describeCommon(name: string, failure: unknown): string | undefined {
  if (name === 'InvalidStateError') {
    // Единственный случай, когда браузер говорит прямо: ключ для этого сайта
    // на устройстве уже есть. Для входа это не помеха, для привязки — помеха.
    return 'На этом устройстве passkey для Onlap уже привязан.';
  }

  if (name === 'SecurityError') {
    return 'Браузер не принял домен: passkey работает только по HTTPS и только на том домене, где был заведён.';
  }

  if (name === 'NotSupportedError' || name === 'AbortError') {
    return 'Этот браузер не поддерживает passkey. Войдите по почте.';
  }

  const message = failure instanceof Error ? failure.message : '';
  return message.length > 0 ? message : undefined;
}

/**
 * `@simplewebauthn/browser` заворачивает отказ браузера в свой `WebAuthnError`,
 * но `name` берёт у причины, так что разбор по имени работает и с обёрткой.
 */
function errorName(failure: unknown): string {
  return failure instanceof Error ? failure.name : '';
}
